import React from 'react';
import PropTypes from 'prop-types';
import { Link, useStaticQuery, graphql } from 'gatsby';
import uuid from 'uuid';
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemButton,
} from 'react-accessible-accordion';
import Chevron from '../../assets/icons/chevron_right.svg';
import _ from 'lodash';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

const eachDeep = require('deepdash/eachDeep');

const Breadcrumbs = ({parentPages, childPages }) => {
  return (
    <div className="breadcrumb-container">
      <nav aria-labelledby="label-breadcrumb" className="breadcrumbs">
        <h3 id="label-breadcrumb" className="sr-only">Breadcrumb</h3>
        <ul>
          { parentPages.map(item => {
            return (
            <li key={uuid()} className={item.current ? 'parent-current' : 'parent'}>
              <Link to={item.url}>
                {item.name}
              </Link>
              {item.current && childPages.length > 0 &&
                <ul>
                  { childPages.map(item => {
                    return (
                    <li key={uuid()} className={cn("child", { current: item.current })}>
                      <Link to={item.url} aria-current={item.current ? "location" : null}>
                        {item.name}
                      </Link>
                    </li>
                  )})}
                </ul>
              }
            </li>
          )})}
        </ul>
      </nav>
    </div>
  )
}

Breadcrumbs.propTypes = {
  parentPages: PropTypes.arrayOf(PropTypes.object),
  childPages: PropTypes.arrayOf(PropTypes.object)
}

const BreadCrumbsMobile = ({parentPages, childPages, withCurrent}) => {
  const { t } = useTranslation()
  return (
    <nav aria-labelledby="label-breadcrumb-mobile" className={withCurrent ? 'with-current breadcrumbs-mobile' : 'breadcrumbs-mobile'}>
      <h3 id="label-breadcrumb-mobile" className="sr-only">Breadcrumb</h3>
      <ul className={withCurrent ? 'with-current' : ''}>
        { parentPages.map(item => {
          return (
          <li key={uuid()}>
            <Link to={item.url}>
              {item.name}
            </Link>
          </li>
        )})}
      </ul>
      {childPages.length > 0 && !_.find(childPages, { current: true}) && (
        <Accordion allowZeroExpanded={true}>
          <AccordionItem>
            <AccordionItemHeading>
              <AccordionItemButton>
                <div className="title">{t('navigation.child_pages')}<span className="accordion__arrow" /></div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              {childPages.map(item => (
                <div key={uuid()}>
                  <Link to={item.url}><Chevron />{item.name}</Link>
                </div>
              ))}
            </AccordionItemPanel>
          </AccordionItem>
        </Accordion>
      )}
    </nav>
  )
}

BreadCrumbsMobile.propTypes = {
  parentPages: PropTypes.arrayOf(PropTypes.object),
  childPages: PropTypes.arrayOf(PropTypes.object),
  withCurrent: PropTypes.bool,
}

const BreadCrumbsWrapper = ({ currentPath, language, breadcrumbs = [] }) => {
  const parentPages = [{ name: 'Home', url: (language === 'en' ? '/en' : '/') }]
  const childPages = [];

  eachDeep(breadcrumbs, (obj, key, parent, context) => {
    if (_.get(obj, 'url.path') === currentPath) {
      context.parents.forEach(parent => {
        if (_.get(parent.value, 'url.path')) {
          parentPages.push({
            name: _.get(parent.value, 'label'),
            url: _.get(parent.value, 'url.path')
          })
        }
      })

      if (_.isEmpty(obj.links)) {
        parentPages[parentPages.length - 1].current = true;
        _.forEach(parent.links, (link) => {
          childPages.push({
            name: _.get(link, 'label'),
            url: _.get(link, 'url.path'),
            current: _.get(link, 'url.path') === currentPath
          })
        });
      } else {
        parentPages.push({
          name: obj.label,
          url: obj.url.path,
          current: true
        });
        _.forEach(obj.links, (link) => {
          childPages.push({
            name: _.get(link, 'label'),
            url: _.get(link, 'url.path')
          })
        });
      }
    }
  }, { childrenPath: 'links' })

  return (
    <React.Fragment>
      <Breadcrumbs key="desktop" parentPages={parentPages} childPages={childPages} />
      <BreadCrumbsMobile key="mobile" parentPages={parentPages} childPages={childPages} />
    </React.Fragment>
  )
}

export { Breadcrumbs, BreadCrumbsMobile, BreadCrumbsWrapper };
